import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AppSidebar } from './AppSidebar';
import { ModernAIAssistantSidebar } from '@/components/chat/ModernAIAssistantSidebar';
import { useSidebar } from '@/contexts/SidebarContext';
import { useAIAssistant } from '@/contexts/AIAssistantContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface SidebarLayoutProps {
  children: ReactNode;
  businesses?: any[];
  onRefreshBusinesses?: () => void;
}

export const SidebarLayout = ({
  children,
  businesses,
  onRefreshBusinesses
}: SidebarLayoutProps) => {
  const { isCollapsed } = useSidebar();
  const aiAssistant = useAIAssistant();
  const isMobile = useIsMobile();
  const location = useLocation();

  const isSettings = location.pathname.startsWith('/settings');
  // Nudge content slightly closer to the sidebar on settings pages
  const mainMarginLeft = isMobile
    ? 0
    : (isCollapsed ? 56 : (isSettings ? 240 : 256));

  return (
    <div className="min-h-screen bg-background">
      {/* Left Sidebar - Desktop Only */}
      <AppSidebar />

      {/* Main Layout - Flex container for content and right sidebar */}
      <div className="flex h-screen">
        {/* Content Area - Flexible width that shrinks when sidebar opens */}
        <main
          className="flex-1 overflow-auto transition-all duration-300 ease-in-out"
          style={{
            marginLeft: mainMarginLeft,
          }}
        >
          {children}
        </main>

        {/* AI Assistant Sidebar - Right Side (hidden on settings pages) */}
        {!isSettings && (
          <ModernAIAssistantSidebar
            businessId={aiAssistant.businessId || ''}
            isOpen={aiAssistant.isOpen}
            onOpenChange={(open) => {
              if (!open) {
                aiAssistant.closeSidebar();
              }
            }}
            onUpdateComplete={aiAssistant.onUpdateComplete}
          />
        )}
      </div>
    </div>
  );
};